"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Mail, User, Building } from "lucide-react"
import { useState } from "react"

export function DemoSection() {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [company, setCompany] = useState("")
  const [error, setError] = useState<string | null>(null)

  const mailto = `mailto:info@octocare.co?subject=Schedule%20a%20demo&body=${encodeURIComponent(
    `Name: ${name}\nEmail: ${email}\nCompany: ${company}`,
  )}`

  const isValidEmail = (value: string) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(value)

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-br from-[#2d86c7]/5 via-white to-[#34D1BE]/5">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl lg:text-4xl font-light text-gray-900 tracking-tight">Ready to transform your Health Insurance Business?</h2>
          <p className="mt-3 text-[0.9rem] sm:text-lg text-gray-600">
            Book a 30-minute demo to experience how Octocare ERP uses AI to streamline operations, reduce costs, and power smarter decisions.
          </p>
        {/*  <p className="mt-2 text-sm text-gray-500">Trusted by HMOs across Africa</p> */}
        </div>

        <Card className="animated-border relative w-full max-w-3xl mx-auto bg-white/70 backdrop-blur-md shadow-xl rounded-3xl transition-all hover:shadow-2xl hover:scale-[1.01]">
          <CardHeader className="pb-0 text-center">
            <CardTitle className="text-2xl font-light">Schedule a demo</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (!isValidEmail(email)) {
                  setError("Please enter a valid work email")
                  return
                }
                setError(null)
                window.location.href = mailto
              }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  aria-label="Your name"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-full bg-white/50 backdrop-blur-md border border-gray/40 pl-10 focus:ring-2 focus:ring-[#1a85ca] focus:border-transparent"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  aria-label="Work email"
                  type="email"
                  placeholder="Work email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-full bg-white/50 backdrop-blur-md border border-gray/40 pl-10 focus:ring-2 focus:ring-[#1a85ca] focus:border-transparent"
                />
              </div>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  aria-label="Company"
                  placeholder="Company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="rounded-full bg-white/50 backdrop-blur-md border border-gray/40 pl-10 focus:ring-2 focus:ring-[#1a85ca] focus:border-transparent"
                />
              </div>
              {error && (
                <p className="md:col-span-3 text-center text-[#EA3056] text-sm">{error}</p>
              )}
              <div className="md:col-span-3 flex justify-center">
                <Button className="group relative overflow-hidden rounded-full bg-[#1a85ca] hover:bg-[#1a85cc] px-6">
                  <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[linear-gradient(120deg,transparent,rgba(255,255,255,.25),transparent)] animate-pulse-slow" />
                  <Mail className="mr-2 h-4 w-4" />
                  Request a 30‑min demo
                </Button>
              </div>
            </form>
            <p className="mt-4 text-sm text-gray-500 text-center">
              Prefer email? <a className="text-[#1a85ca] font-medium hover:underline" href="mailto:info@octocare.co">info@octocare.co</a>
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  )
}
